import React, { useContext, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Container, Typography, Box, Divider, Paper, Stack, Button, IconButton } from "@mui/material";

import InfoSection, { InformationField } from "../../components/general/InfoSection";
import EventChip from "../../components/events/EventChip";

import useResponsive from "../../hooks/useResponsive";
import { CheckCircle, Group, Grade, Edit, Delete } from "@mui/icons-material";
import { doesPermissionsHaveSomeOf } from "../../utils/authUtils";
import dayjs from "dayjs";
import WorkshopInstanceAdmissionClosingTable from "../../components/workshops/workshopInstance/WorkshopInstanceAdmissionClosingTable";
import WorkshopInstanceAttendanceTable from "../../components/workshops/workshopInstance/WorkshopInstanceAttendanceTable";
import { AuthContext } from "../../context/auth-context/AuthContext";
import { useWorkshopDetails } from "../../hooks/workshops/useWorkshopDetails";
import useWorkshopInstanceDetails from "../../hooks/workshops/useWorkshopInstanceDetails";
import { UpdateWorkshopInstanceDto, WorkshopInstanceDto, WorkshopParticipantDto } from "../../types/workshopTypes";
import WorkshopParticipants from "../../components/workshops/workshopInstance/WorkshopParticipants";
import AddWorkshopMembersField from "../../components/workshops/workshopInstance/AddWorkshopMembersField";
import useMembers from "../../hooks/members/useMembers";
import WorkshopInstanceDialog from "../../components/workshops/workshopInstance/WorkshopInstanceDialog";
import { MembersFieldDto } from "../../types/membersTypes";
import DeleteWorkshopInstanceDialog from "../../components/workshops/workshopInstance/DeleteWorkshopInstanceDialog";
import EventRegistrationButton from "../../components/events/EventRegistrationButton";

/**
 * Displays the details of a workshop instance
 * @returns Displays the details of a workshop instance
 */
const WorkshopInstanceDetails = () => {
  const { workshopID, id } = useParams<{ workshopID: string; id: string }>();
  const { auth } = useContext(AuthContext);
  const [isRegistraionClosing, setIsRegistrationClosing] = useState<boolean>(false);
  const [isEditingParticipants, setIsEditingParticipants] = useState<boolean>(false);
  const [isEditingDialogOpen, setIsEditingDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);

  const { workshopDetails } = useWorkshopDetails(Number(workshopID));
  const {
    workshopInstanceDetails,
    addWorkshopParticipant,
    removeWorkshopParticipant,
    updateWorkshopInstance,
    deleteWorkshopInstance,
    closeAdmission,
    completeAttendance,
  } = useWorkshopInstanceDetails(Number(workshopID), Number(id));
  const { members } = useMembers();

  // Member options for the referents autocomplete and to add  as participants
  const membersOptions: MembersFieldDto[] = members.map((member) => ({
    memberId: member.memberId,
    firstname: member.firstname,
    lastname: member.lastname,
  }));

  // Check for the workshop permission
  const hasWorkshopPermission = doesPermissionsHaveSomeOf(auth.permissions, [4]);

  // Check if the user is a referent of the workshop instance
  const isReferent = workshopInstanceDetails?.referents.some((referent) => referent.memberId === auth.userID);

  const isMobile = useResponsive("down", "sm");

  /**
   * function is called to open the edit dialog
   */
  const handleOpenEditDialog = () => {
    setIsEditingDialogOpen(true);
  };

  /**
   * function is called to close the admission
   */
  const handleOpenAdmissionClosingTable = () => {
    setIsRegistrationClosing(true);
  };

  const handleCloseAdmission = async (selected: WorkshopParticipantDto[]) => {
    try {
      await closeAdmission(selected);
    } catch {
      // toast already shown by hook
    } finally {
      setIsRegistrationClosing(false);
    }
  };

  const handleCompleteAttendance = async (selected: WorkshopParticipantDto[]) => {
    try {
      await completeAttendance(selected);
    } catch {
      // toast already shown by hook
    } finally {
      setIsEditingParticipants(false);
    }
  };

  /**
   * function is called to edit the attendance list
   */
  const handleAttendanceList = () => {
    setIsEditingParticipants(true);
  };

  /**
   * function is called to save the workshop instance
   */
  const handleSave = (workshopInstance: UpdateWorkshopInstanceDto) => {
    if (workshopDetails && workshopInstanceDetails) {
      updateWorkshopInstance(workshopDetails.workshopId, workshopInstanceDetails.workshopInstanceId, workshopInstance);
    }
  };

  /**
   * function is called to delete the workshop instance
   */
  const handleDelete = async () => {
    if (workshopDetails && workshopInstanceDetails) {
      try {
        await deleteWorkshopInstance(workshopDetails.workshopId, workshopInstanceDetails.workshopInstanceId);
      } catch {
        // toast already shown by hook
      } finally {
        setIsDeleteDialogOpen(false);
      }
    }
  };

  /**
   * function is called to sign up for the event
   * @returns void
   */
  const handleSignUpForEvent = async () => {
    const currentUser = membersOptions.find((member) => member.memberId === auth.userID);

    // If the workshop instance details are not loaded, return
    if (!workshopInstanceDetails) {
      return;
    }

    // If the user is already signed up for the event, return
    if (workshopInstanceDetails.participants.some((participant) => participant.memberId === auth.userID)) {
      return;
    }

    // If the user is not found in the members options, return
    if (!currentUser) {
      return;
    }

    // Try to add the user as a participant to the event
    try {
      await addWorkshopParticipant(currentUser);
    } catch {
      // toast already shown by hook
    }
  };

  /**
   * function is called to sign out from the event
   * @returns void
   */
  const handleSignOutFromEvent = async () => {
    const currentUser = membersOptions.find((member) => member.memberId === auth.userID);

    // If the workshop instance details are not loaded, return
    if (!workshopInstanceDetails) {
      return;
    }

    // If the user is not signed up for the event, return
    if (!workshopInstanceDetails.participants.some((participant) => participant.memberId === auth.userID)) {
      return;
    }

    // If the user is not found in the members options, return
    if (!currentUser) {
      return;
    }

    // Try to remove the user from the participants of the event
    try {
      await removeWorkshopParticipant(currentUser);
    } catch {
      // toast already shown by hook
    }
  };

  /**
   * function to check if the registration for the event is open
   * @returns boolean indicating whether the registration for the event is open or not
   */
  const checkIfRegistrationIsOpen = (): boolean => {
    // If the workshop instance details are not loaded, return false
    if (!workshopInstanceDetails) {
      return false;
    }

    // If the status of the workshop instance is not "Anmeldung", return false
    if (workshopInstanceDetails.status !== "Anmeldung") {
      return false;
    }

    const now = dayjs();
    const registrationFrom = workshopInstanceDetails.registrationFrom
      ? dayjs(workshopInstanceDetails.registrationFrom)
      : null;
    const registrationUntil = workshopInstanceDetails.registrationUntil
      ? dayjs(workshopInstanceDetails.registrationUntil)
      : null;

    // If the date of the workshop instance is in the past, return false
    if (dayjs(workshopInstanceDetails.date).isBefore(now)) {
      return false;
    }

    // If neither registration from nor registration until dates are set, return false
    if (!workshopInstanceDetails.registrationFrom && !workshopInstanceDetails.registrationUntil) {
      return false;
    }

    // If both registration from and until dates are set, check if the current date is between them
    if (registrationFrom && registrationUntil) {
      return now.isAfter(registrationFrom) && now.isBefore(registrationUntil);
    }

    // If only the registration from date is set, check if the current date is after it
    if (registrationFrom && !registrationUntil) {
      return now.isAfter(registrationFrom);
    }

    // If only the registration until date is set, check if the current date is before it
    if (!registrationFrom && registrationUntil) {
      return now.isBefore(registrationUntil);
    }

    return false;
  };

  const WorkshopInstanceAdditionalButtons: React.FunctionComponent<WorkshopInstanceDto> = (
    props: WorkshopInstanceDto
  ) => {
    const workshopInstance = props;
    const isMobile = useResponsive("down", "sm");

    return workshopInstance.status === "Anmeldung" ? (
      <Button
        variant={"contained"}
        onClick={handleOpenAdmissionClosingTable}
        sx={{ fontWeight: 600, fontSize: isMobile ? 10 : 14 }}
        color="info"
        startIcon={<CheckCircle />}
      >
        {isMobile ? "abschließen" : "Anmeldung schließen"}
      </Button>
    ) : workshopInstance.status === "Anmeldung abgeschlossen" ? (
      <Button
        variant="contained"
        sx={{ fontWeight: 600, fontSize: isMobile ? 10 : 14 }}
        color="info"
        onClick={handleAttendanceList}
        startIcon={<Group />}
      >
        {isMobile ? "Anwesenheit" : "Anwesenheitsliste eingeben"}
      </Button>
    ) : workshopInstance.status === "Feedback" || workshopInstance.status === "Abgeschlossen" ? (
      <Button
        variant="contained"
        component={Link}
        to={`/workshops/${workshopInstance.workshopId}/${workshopInstance.workshopInstanceId}/feedbackauswertung`}
        sx={{ fontWeight: 600, fontSize: isMobile ? 10 : 14 }}
        color="info"
        startIcon={<Grade />}
      >
        {isMobile ? "Feedback" : "Feedback anzeigen"}
      </Button>
    ) : null;
  };

  /**
   * Function to render the registration period of the event in a user friendly way, checks if the registration from and to dates are set and formats them accordingly
   * @returns A string representing the registration period of the event, formatted in a user friendly way, if the registration from and to dates are not set, returns "-"
   */
  const renderRegistrationPeriod = (): string => {
    if (!workshopInstanceDetails) {
      return "-";
    }
    const registrationFrom = workshopInstanceDetails.registrationFrom
      ? dayjs(workshopInstanceDetails.registrationFrom).locale("de").format("DD.MM.YYYY HH:mm")
      : null;
    const registrationTo = workshopInstanceDetails.registrationUntil
      ? dayjs(workshopInstanceDetails.registrationUntil).locale("de").format("DD.MM.YYYY HH:mm")
      : null;
    if (registrationFrom && registrationTo) {
      return `${registrationFrom} - ${registrationTo}`;
    }

    if (registrationFrom && !registrationTo) {
      return `ab ${registrationFrom}`;
    }

    if (!registrationFrom && registrationTo) {
      return `bis ${registrationTo}`;
    }
    return "-";
  };

  const instanceFields: Array<InformationField> = [
    {
      label: "Workshop",
      value: workshopDetails ? workshopDetails?.workshopName : null,
      type: "text",
    },
    {
      label: "Status",
      value: workshopInstanceDetails ? workshopInstanceDetails.status : null,
      type: "text",
    },
    {
      label: "Anmeldung",
      value: renderRegistrationPeriod(),
      type: "text",
    },
    {
      label: "Datum",
      value: workshopInstanceDetails ? dayjs(workshopInstanceDetails.date).format("DD.MM.YYYY") : null,
      type: "text",
    },
    {
      label: "Startzeit",
      value: workshopInstanceDetails?.startTime ? dayjs(workshopInstanceDetails.startTime).format("HH:mm") : null,
      type: "text",
    },
    {
      label: "Endzeit",
      value: workshopInstanceDetails?.endTime ? dayjs(workshopInstanceDetails.endTime).format("HH:mm") : null,
      type: "text",
    },
    {
      label: "Ort",
      value: workshopInstanceDetails ? workshopInstanceDetails.location : null,
      type: "text",
    },
    {
      label: "Zielgruppe",
      value: workshopInstanceDetails ? workshopInstanceDetails.targetGroup : null,
      type: "text",
    },
    {
      label: "Referent",
      value: workshopInstanceDetails
        ? workshopInstanceDetails.referents.map((referent) => `${referent.firstname} ${referent.lastname}`).join(", ")
        : null,
      type: "text",
    },
    {
      label: "Max. Teilnehmerzahle",
      value: workshopInstanceDetails ? workshopInstanceDetails.maximumParticipants?.toString() : null,
      type: "text",
    },
  ];

  return (
    <Container>
      {workshopDetails && workshopInstanceDetails ? (
        <WorkshopInstanceDialog
          workshop={workshopDetails}
          workshopInstance={workshopInstanceDetails}
          open={isEditingDialogOpen}
          onClose={() => setIsEditingDialogOpen(false)}
          onSave={handleSave}
          members={membersOptions}
        />
      ) : null}
      {workshopDetails && workshopInstanceDetails ? (
        <DeleteWorkshopInstanceDialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onDelete={handleDelete}
          workshopInstance={workshopInstanceDetails}
          workshop={workshopDetails}
        />
      ) : null}
      {isRegistraionClosing ? (
        <WorkshopInstanceAdmissionClosingTable
          participants={workshopInstanceDetails?.participants || []}
          setIsRegistrationClosing={setIsRegistrationClosing}
          closeAdmission={handleCloseAdmission}
        />
      ) : isEditingParticipants ? (
        <WorkshopInstanceAttendanceTable
          participants={workshopInstanceDetails?.participants || []}
          setIsEditingParticipants={setIsEditingParticipants}
          completeAttendance={handleCompleteAttendance}
        />
      ) : workshopDetails && workshopInstanceDetails ? (
        <>
          <Stack direction={"column"} justifyContent={"space-between"} alignItems={"flex-start"} sx={{ mb: 1 }}>
            <Typography variant="h5" component="h1" gutterBottom fontWeight={"bold"}>
              Informationen zum Workshop-Termin
            </Typography>
            {hasWorkshopPermission || isReferent ? (
              <Stack direction={"row"} spacing={isMobile ? 2 : 2}>
                {isMobile ? (
                  <Button
                    variant="contained"
                    onClick={handleOpenEditDialog}
                    startIcon={<Edit />}
                    sx={{ fontSize: 10 }}
                    color={"primary"}
                  >
                    {"bearbeiten"}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color={"primary"}
                    sx={{ fontWeight: 600, mr: 2 }}
                    onClick={handleOpenEditDialog}
                    startIcon={<Edit />}
                    size="small"
                  >
                    Bearbeiten
                  </Button>
                )}
                <WorkshopInstanceAdditionalButtons {...workshopInstanceDetails} />
                {hasWorkshopPermission ? (
                  isMobile ? (
                    <IconButton onClick={() => setIsDeleteDialogOpen(true)}>
                      <Delete color="error" />
                    </IconButton>
                  ) : (
                    <Button
                      variant="contained"
                      color="error"
                      sx={{ fontWeight: 600, mr: 2 }}
                      onClick={() => setIsDeleteDialogOpen(true)}
                      startIcon={<Delete />}
                      size="small"
                    >
                      Workshop-Termin löschen
                    </Button>
                  )
                ) : null}
              </Stack>
            ) : null}
          </Stack>
          <Paper>
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              sx={{ pt: 2, ml: 3, mr: 3 }}
              alignItems={"center"}
            >
              <Typography variant="h6" color="primary" fontWeight={"bold"}>
                Details
              </Typography>
              <EventChip type={workshopDetails?.type || "Sonstige"} sx={{ ml: 3 }} size="medium" />
            </Stack>
            <Box sx={{ ml: 3, mr: "auto", pt: 1, pb: 4, maxWidth: 600 }}>
              <InfoSection fields={instanceFields} />
            </Box>
            {workshopInstanceDetails?.participants.length > 0 ? (
              <>
                <Divider light sx={{ width: "95%", margin: "auto", borderColor: "#f6891f" }} />
                <Typography variant="h6" color="primary" fontWeight={"bold"} sx={{ pt: 2, ml: 3 }}>
                  Teilnehmerliste ({workshopInstanceDetails?.participants.length})
                </Typography>
                <Box sx={{ ml: 3, mr: 3, pb: 3, pt: 1 }}>
                  <WorkshopParticipants
                    participants={workshopInstanceDetails?.participants || []}
                    removeParticipant={removeWorkshopParticipant}
                    hasPermissions={hasWorkshopPermission}
                    status={workshopInstanceDetails?.status}
                  />
                </Box>
              </>
            ) : null}
          </Paper>
          <Stack
            sx={{
              justifyContent: "space-between",
              mt: 2,
            }}
            alignItems={isMobile ? "start" : "center"}
            direction={isMobile ? "column" : "row"}
            spacing={isMobile ? 2 : 0}
          >
            <>
              {workshopInstanceDetails?.status === "Anmeldung" &&
              dayjs(workshopInstanceDetails.date).isAfter(dayjs()) ? (
                <EventRegistrationButton
                  isUserSignedUp={workshopInstanceDetails?.participants.some(
                    (participant) => participant.memberId === auth.userID
                  )}
                  isRegistrationOpen={checkIfRegistrationIsOpen()}
                  isRegistrationFull={
                    workshopInstanceDetails.maximumParticipants
                      ? workshopInstanceDetails.participants.length >= workshopInstanceDetails.maximumParticipants
                      : false
                  }
                  onSignUp={handleSignUpForEvent}
                  onSignOut={handleSignOutFromEvent}
                  size={"medium"}
                />
              ) : null}
              {hasWorkshopPermission ? (
                <AddWorkshopMembersField
                  members={membersOptions}
                  participants={workshopInstanceDetails?.participants || []}
                  addParticipant={addWorkshopParticipant}
                />
              ) : null}
            </>
          </Stack>
        </>
      ) : null}
    </Container>
  );
};

export default WorkshopInstanceDetails;
